import React, { useEffect, useRef, useState } from 'react';
import Slider from 'rc-slider';
import moment from 'moment-timezone';
import { Popover } from '../Popover';
import { useTimezone } from '../../hooks/useTimezone';
import useRestaurantUuid from '../../hooks/store/useRestaurantUuid';
import 'rc-slider/assets/index.css';

const PlayerActivityHistoryRange = ({ availableHistoryRange, historyRange, setHistoryRange }) => {
  const [range, setRange] = useState({ start: availableHistoryRange.start, end: availableHistoryRange.end });
  const [timelineRange, setTimelineRange] = useState([availableHistoryRange.start, availableHistoryRange.end]);

  const timelineRef = useRef(null);
  const popoverRef = useRef(null);
  const timelineChangingRef = useRef(false);
  const rangeStartRef = useRef(0);
  const rangeEndRef = useRef(0);
  const handleNodesRef = useRef([]);
  const labelRefs = useRef([]);
  
  const restaurantUuid = useRestaurantUuid();
  const timezone = useTimezone(restaurantUuid);
  const isSingleEvent = range.start === range.end;

  rangeStartRef.current = range.start;
  rangeEndRef.current = range.end;

  const formatTs = ts => {
    return moment.utc(ts).tz(timezone).format('hh:mm A');
  };

  const onTimelineMouseDownCapture = event => {
    if (event.button === 2) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onTimelineMouseMove = event => {
    if (timelineRef.current && popoverRef.current && !timelineChangingRef.current) {
      const tooltip = popoverRef.current;
      const start = rangeStartRef.current;
      const end = rangeEndRef.current;
      const rect = timelineRef.current.getBoundingClientRect();
      const percent = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      const diffTs = end - start;
      const popoverTs = Math.floor(start + percent * diffTs);
      const translateX = event.clientX - tooltip.offsetWidth / 2;

      if (tooltip.classList.contains('slider-popover-hidden')) {
        tooltip.classList.remove('slider-popover-hidden');
      }

      tooltip.textContent = formatTs(popoverTs);
      tooltip.style.transform = `translate(${translateX}px, ${rect.top}px)`;
    }
  };

  const onTimelineMouseLeave = () => {
    if (popoverRef.current) {
      popoverRef.current.textContent = '';
    }
  };

  const onBeforeChange = () => {
    timelineChangingRef.current = true;

    if (popoverRef.current) {
      popoverRef.current.classList.add('slider-popover-hidden');
    }
  };

  const onChangeComplete = newTimelineRange => {
    timelineChangingRef.current = false;
    setTimelineRange(newTimelineRange);
    setHistoryRange({ start: newTimelineRange[0], end: newTimelineRange[1] });
  };
  
  const handleRender = (node, props) => {
    const { value, index } = props;
    handleNodesRef.current[index] = node;
    
    return (
      <>
        {node}
        
        <div
          ref={el => {
            if (el) {
              labelRefs.current[index] = el;
            }
          }}
          className="activity-history-range-handle-label"
          style={{ left: node.props.style.left }}
        >
          {formatTs(value)}
        </div>
      </>
    );
  };
  
  useEffect(() => {
    if (historyRange) {
      setTimelineRange([
        historyRange.payload.start,
        historyRange.payload.end,
      ]);
    } else {
      setTimelineRange([
        availableHistoryRange.start,
        availableHistoryRange.end,
      ]);
    }
    
    setRange({ start: availableHistoryRange.start, end: availableHistoryRange.end });
  }, [availableHistoryRange]);
  
  useEffect(() => {
    const container = timelineRef.current;
    const [leftEl, rightEl] = labelRefs.current;
    
    if (!container || !leftEl || !rightEl) {
      return;
    }
    
    const containerWidth = container.offsetWidth;
    const [timelineStart, timelineEnd] = timelineRange;
    const rangeTotal = range.end - range.start;
    
    if (!rangeTotal || !containerWidth) {
      return;
    }
    
    const GAP = 12;
    const BORDER_EDGE = -8;
    const EPS = 0.5;
    
    const leftPx = ((timelineStart - range.start) / rangeTotal) * containerWidth;
    const rightPx = ((timelineEnd - range.start) / rangeTotal) * containerWidth;
    
    const leftWidth = leftEl.offsetWidth;
    const rightWidth = rightEl.offsetWidth;
    
    const leftMin = leftWidth / 2 + BORDER_EDGE;
    const leftMax = containerWidth - leftWidth / 2 - GAP;
    
    const rightMin = rightWidth / 2 + GAP;
    const rightMax = containerWidth - rightWidth / 2 - BORDER_EDGE;
    
    let left = leftPx;
    let right = rightPx;
    
    // ============================
    // SYMMETRICAL BREEDING
    // ============================
    const minDistance = (leftWidth + rightWidth) / 2 + GAP;
    const distance = right - left;
    
    if (distance < minDistance) {
      const shift = (minDistance - distance) / 2;
      left -= shift;
      right += shift;
    }
    
    // ============================
    // CHECK BORDERS BEFORE REDISTRIBUTION
    // ============================
    const leftClamped = left < leftMin || left > leftMax;
    const rightClamped = right < rightMin || right > rightMax;
    
    // ============================
    // EDGE-PRIORITY CLAMP
    // ============================
    if (left < leftMin) {
      left = leftMin;
    }
    
    if (left > leftMax) {
      left = leftMax;
    }
    
    if (right < rightMin) {
      right = rightMin;
    }
    
    if (right > rightMax) {
      right = rightMax;
    }
  
    // ============================
    // REDISTRIBUTE OVERFLOW STRICTLY ACCORDING TO PRIORITY
    // ============================
    if (leftClamped && !rightClamped) {
      right = rightPx; // fix right
    }
    
    if (rightClamped && !leftClamped) {
      left = leftPx; // fix left
    }
  
    // ============================
    // CHECKING FOR STUCK
    // ============================
    const leftStuck = left <= leftMin;
    const rightStuck = right >= rightMax;
    const finalDistance = right - left;
    const stuckTogether = finalDistance < minDistance + EPS;
    
    const moveLeftElement = () => {
      const leftTransform = left - leftPx;
      const nodeLeft =  handleNodesRef.current[0]?.props?.style?.left;
      
      if (nodeLeft) {
        leftEl.style.left = nodeLeft;
      }
      
      leftEl.style.transform = `translateX(-50%) translateX(${leftTransform}px)`;
    };
    
    const moveRightElement = () => {
      const rightTransform = right - rightPx;
      const nodeLeft = handleNodesRef.current[1]?.props?.style?.left;
      
      if (nodeLeft) {
        rightEl.style.left = nodeLeft;
      }
      
      rightEl.style.transform = `translateX(-50%) translateX(${rightTransform}px)`;
    };
  
    // ============================
    // DO NOT TRANSFORM ELEMENTS IF THEY ARE STUCK TO PREVENT MICRO LEFT/RIGHT MOVEMENTS
    // ============================
    if (leftStuck) {
      leftEl.style.left = '-8px';
      leftEl.style.transform = 'translateX(0)';
      
      if (stuckTogether) {
        rightEl.style.left = `${leftWidth + GAP + BORDER_EDGE}px`;
        rightEl.style.transform = `translateX(0)`;
      } else {
        moveRightElement();
      }
    } else if (rightStuck) {
      rightEl.style.left = `calc(100% - ${rightWidth + BORDER_EDGE}px)`;
      rightEl.style.transform = `translateX(0)`;
      
      if (stuckTogether) {
        leftEl.style.left = `calc(100% - ${leftWidth + GAP + rightWidth + BORDER_EDGE}px)`;
        leftEl.style.transform = `translateX(0)`;
      } else {
        moveLeftElement();
      }
    } else {
      moveLeftElement();
      moveRightElement();
    }
  }, [timelineRange, range.start, range.end]);
  
  if (isSingleEvent) {
    return (
      <div className="activity-history-range">
        <div className="activity-history-range-single">
          <div className="activity-history-range-single-rail" />
          <div className="activity-history-range-single-dot" />
          <div className="activity-history-range-single-label">
            {formatTs(range.start)}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="activity-history-range">
      <div
        className="slider-timeline"
        onMouseDownCapture={onTimelineMouseDownCapture}
        onMouseMove={onTimelineMouseMove}
        onMouseLeave={onTimelineMouseLeave}
        ref={timelineRef}
      >
        <Popover.Wrapper>
          <Slider
            range
            min={range.start}
            max={range.end}
            value={timelineRange}
            pushable={60000} // 1 minute
            onBeforeChange={onBeforeChange}
            onChange={setTimelineRange}
            onChangeComplete={onChangeComplete}
            allowCross={false}
            keyboard={false}
            handleRender={handleRender}
          />

          <Popover position="top" className="slider-popover" popoverRef={popoverRef} />
        </Popover.Wrapper>
      </div>
    </div>
  );
};

export default PlayerActivityHistoryRange;

